import React, { useEffect } from 'react'

import styled from '@emotion/styled'

import { AlternativeOptionItem } from './AlternativeOptionItem'

import { cssRecipeList, cssRecipeListText, cssVariantsTitle } from './styles'
import { AlternativeOptions } from '../../../model/alternativeOptions/alternativeOptions'
import { queueTrackingEvent } from '@library/tracking-integration'
import { Recipe, RecipeCoreId } from '@library/menu-service'
import { Color, Spinner, Box, Display, JustifyContent } from '@gousto-internal/citrus-react'

export type TrackVariantDisplay = {
  action: 'recipe_variant_action_sheet'
  view: string
}

type RecipeAlternativeOptionsProps = {
  currentRecipeCoreId: RecipeCoreId
  /**
   * Optional Function to be called upon switching recipes.
   */
  onChangeCheckedRecipe: (_: { previousRecipeId: RecipeCoreId; nextRecipeId: RecipeCoreId }) => void
  selectVariant: ({
    variant,
    variantOutOfStock,
  }: {
    variant: Recipe
    variantOutOfStock: boolean
  }) => void
  options: AlternativeOptions['options']
  isMobile?: boolean
  isLoading?: boolean
}

const RecipeListDiv = styled.div(cssRecipeList)
const VariantsTitle = styled.h2(cssVariantsTitle)
const RecipeListText = styled.ul(cssRecipeListText)

export function RecipeAlternativeOptions({
  currentRecipeCoreId,
  onChangeCheckedRecipe,
  selectVariant,
  options,
  isMobile = false,
  isLoading = false,
}: RecipeAlternativeOptionsProps) {
  useEffect(() => {
    if (!options || options.length <= 1) {
      return
    }

    queueTrackingEvent<TrackVariantDisplay>({
      action: 'recipe_variant_action_sheet',
      view: 'grid',
    })
  }, [options])

  if (options && options.length <= 1) {
    return null
  }

  const preventPropagation = (e: React.SyntheticEvent) => e.stopPropagation()

  return (
    <RecipeListDiv
      role="button"
      tabIndex={-1}
      onClick={preventPropagation}
      onKeyPress={preventPropagation}
    >
      {!isMobile && <VariantsTitle>Choose an option</VariantsTitle>}

      {isLoading || !options ? (
        <Box
          width="100%"
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
          paddingV={5}
        >
          <Spinner color={Color.ColdGrey_900} size={6} />
        </Box>
      ) : (
        <RecipeListText>
          {options.map(({ recipe, recipeName, isChecked, isOutOfStock, surcharge }) => (
            <AlternativeOptionItem
              key={recipe.coreId}
              recipeId={recipe.coreId}
              recipeName={recipeName}
              onChange={() => {
                selectVariant({ variant: recipe, variantOutOfStock: isOutOfStock })
                onChangeCheckedRecipe({
                  previousRecipeId: currentRecipeCoreId,
                  nextRecipeId: recipe.coreId,
                })
              }}
              isChecked={isChecked}
              isOutOfStock={isOutOfStock}
              surcharge={surcharge === undefined ? null : surcharge}
            />
          ))}
        </RecipeListText>
      )}
    </RecipeListDiv>
  )
}
